import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AffiliateService } from '../../api/generated/services/AffiliateService';
import type { CouponCreateRequest } from '../../api/generated/models/CouponCreateRequest';
import type { CouponRedemptionView } from '../../api/generated/models/CouponRedemptionView';
import type { CouponView } from '../../api/generated/models/CouponView';
import { ApiError } from '../../api/generated/core/ApiError';
import { helper } from '../../utils/helper';
import type { RootState } from '../store';

/**
 * Local types for the affiliate cabinet endpoints. The generated client
 * declares these responses as `Record<string, any>`, so the shapes below
 * mirror the affiliate contract exactly. All money amounts are in cents.
 */
export interface AffiliateMeView {
  id: string;
  name: string;
  companyName: string | null;
  refCode: string;
  /** Ready-to-share referral link built by the backend - shown/copied as is. */
  referralLink: string;
  commissionPercent: number;
}

export interface AffiliateStatsView {
  registrations: number;
  trials: number;
  paying: number;
  revenueNetto: number;
  currency: string;
  commission: {
    percent: number;
    totalAccrued: number;
    available: number;
    onHold: number;
  };
}

export type AffiliateReferralStatus = 'registered' | 'trial' | 'paying' | 'churned' | 'deleted';

export interface AffiliateReferralItemView {
  /**
   * Always masked by the backend (e.g. `a***@firma.de`) - shown as is.
   * null = the referral's account was deleted (commissions are snapshotted
   * and survive the deletion).
   */
  email: string | null;
  /** null for deleted accounts. */
  registeredAt: string | null;
  status: AffiliateReferralStatus;
  /** Deleted records only: ISO date of the referral's last payment. */
  lastPaidAt?: string | null;
  revenueNetto: number;
  commissionAccrued: number;
}

export interface AffiliateReferralsView {
  items: AffiliateReferralItemView[];
  total: number;
  page: number;
  limit: number;
}

/**
 * `idle` - /me not requested yet; `partner` / `not_partner` - resolved once
 * per session. 403 ("Current user is not an active partner") simply means the
 * partner section is not shown; any other failure also resolves to
 * `not_partner` so the feature detection can never loop.
 */
type PartnerStatus = 'idle' | 'loading' | 'partner' | 'not_partner';

interface AffiliateState {
  partnerStatus: PartnerStatus;
  me: AffiliateMeView | null;
  stats: AffiliateStatsView | null;
  statsLoading: boolean;
  referrals: AffiliateReferralsView | null;
  referralsLoading: boolean;
  coupons: CouponView[];
  couponsLoading: boolean;
  /** Redemptions per coupon id, loaded on demand when a row is expanded. */
  couponRedemptions: Record<string, CouponRedemptionView[]>;
}

const initialState: AffiliateState = {
  partnerStatus: 'idle',
  me: null,
  stats: null,
  statsLoading: false,
  referrals: null,
  referralsLoading: false,
  coupons: [],
  couponsLoading: false,
  couponRedemptions: {},
};

/**
 * One-shot feature detection + profile fetch (GET /v2/api/affiliate/me).
 * The `condition` guard makes repeated dispatches (layout + cabinet page)
 * no-ops once the status is resolved.
 */
export const fetchAffiliateMe = createAsyncThunk<AffiliateMeView, void, { state: RootState }>(
  'affiliate/fetchMe',
  async () => {
    const response = await AffiliateService.getAppAffiliateMeGet();
    return response as AffiliateMeView;
  },
  {
    condition: (_, { getState }) => getState().affiliate.partnerStatus === 'idle',
  }
);

export const fetchAffiliateStats = createAsyncThunk<
  AffiliateStatsView,
  { from?: string; to?: string }
>('affiliate/fetchStats', async ({ from, to }) => {
  const response = await AffiliateService.getAppAffiliateMeStats(from, to);
  return response as AffiliateStatsView;
});

export const fetchAffiliateReferrals = createAsyncThunk<
  AffiliateReferralsView,
  { page: number; limit: number }
>('affiliate/fetchReferrals', async ({ page, limit }) => {
  const response = await AffiliateService.getAppAffiliateMeReferrals(page, limit);
  return response as AffiliateReferralsView;
});

export const fetchAffiliateCoupons = createAsyncThunk<CouponView[]>(
  'affiliate/fetchCoupons',
  async () => {
    const response = await AffiliateService.getAppAffiliateMeCouponsList();
    return response.items ?? [];
  }
);

/**
 * 400 (partner caps / duplicate code / unknown category) carries the text in
 * `error`; it is handed over via `rejectWithValue` because a plain throw is
 * serialized by RTK and the `ApiError` body would be lost for `unwrap()`.
 */
export const createAffiliateCoupon = createAsyncThunk<
  CouponView,
  CouponCreateRequest,
  { rejectValue: string }
>('affiliate/createCoupon', async (request, { rejectWithValue }) => {
  try {
    return await AffiliateService.postAppAffiliateMeCouponsCreate(request);
  } catch (err) {
    if (err instanceof ApiError && err.status === 400) {
      const body = helper.parseApiErrorBody(err);
      if (body?.error) return rejectWithValue(String(body.error));
    }
    throw err;
  }
});

export const deactivateAffiliateCoupon = createAsyncThunk<CouponView, string>(
  'affiliate/deactivateCoupon',
  async (couponId) => AffiliateService.postAppAffiliateMeCouponsDeactivate(couponId)
);

export const fetchAffiliateCouponRedemptions = createAsyncThunk<
  { couponId: string; items: CouponRedemptionView[] },
  string
>('affiliate/fetchCouponRedemptions', async (couponId) => {
  const response = await AffiliateService.getAppAffiliateMeCouponsRedemptions(couponId);
  return { couponId, items: response.items ?? [] };
});

const affiliateSlice = createSlice({
  name: 'affiliate',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAffiliateMe.pending, (state) => {
        state.partnerStatus = 'loading';
      })
      .addCase(fetchAffiliateMe.fulfilled, (state, action) => {
        state.partnerStatus = 'partner';
        state.me = action.payload;
      })
      .addCase(fetchAffiliateMe.rejected, (state) => {
        state.partnerStatus = 'not_partner';
        state.me = null;
      })
      .addCase(fetchAffiliateStats.pending, (state) => {
        state.statsLoading = true;
      })
      .addCase(fetchAffiliateStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.stats = action.payload;
      })
      .addCase(fetchAffiliateStats.rejected, (state) => {
        state.statsLoading = false;
        state.stats = null;
      })
      .addCase(fetchAffiliateReferrals.pending, (state) => {
        state.referralsLoading = true;
      })
      .addCase(fetchAffiliateReferrals.fulfilled, (state, action) => {
        state.referralsLoading = false;
        state.referrals = action.payload;
      })
      .addCase(fetchAffiliateReferrals.rejected, (state) => {
        state.referralsLoading = false;
      })
      .addCase(fetchAffiliateCoupons.pending, (state) => {
        state.couponsLoading = true;
      })
      .addCase(fetchAffiliateCoupons.fulfilled, (state, action) => {
        state.couponsLoading = false;
        state.coupons = action.payload;
      })
      .addCase(fetchAffiliateCoupons.rejected, (state) => {
        state.couponsLoading = false;
      })
      .addCase(createAffiliateCoupon.fulfilled, (state, action) => {
        state.coupons.unshift(action.payload);
      })
      .addCase(deactivateAffiliateCoupon.fulfilled, (state, action) => {
        const idx = state.coupons.findIndex((c) => c.id === action.payload.id);
        if (idx >= 0) state.coupons[idx] = action.payload;
      })
      .addCase(fetchAffiliateCouponRedemptions.fulfilled, (state, action) => {
        state.couponRedemptions[action.payload.couponId] = action.payload.items;
      });
  },
});

export default affiliateSlice.reducer;
